<script setup lang="ts">
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import type { ISatPriceBreakdownProps } from "@chatfood/design-system";
import { SatPriceBreakdown } from "@chatfood/design-system";
import { computed } from "vue";
import type { IPriceBreakdownProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useDomainLabel } from "~/composables/use-domain-label";

const css = bemBuilder("price-breakdown");
const { t } = useLocale();
const { feeChargeLabel } = useDomainLabel();

const props = defineProps<IPriceBreakdownProps>();
const basket = useBasketStore();

const extractFeeRanges = ():
  | Pick<ISatPriceBreakdownProps["items"][0], "strikedAmount" | "subline">
  | undefined => {
  const coverage = basket.deliveryCoverage;
  const ranges = coverage?.deliveryFeeRanges?.filter(
    (range) => range.basketValue.value > 0,
  );

  if (!coverage || !ranges?.length) return;

  const { deliveryFee } = coverage;
  const sortedRanges = ranges.sort(
    (a, b) => a.basketValue.value - b.basketValue.value,
  );
  const basketChargedDeliveryFee = basket.totalBreakdown.deliveryFee.value;

  const strikedAmount =
    basketChargedDeliveryFee >= deliveryFee.value ? undefined : deliveryFee;

  const { basketValue, fee } =
    sortedRanges.find((range) => range.fee.value < basketChargedDeliveryFee) ||
    sortedRanges[sortedRanges.length - 1];

  return {
    strikedAmount,
    subline:
      basketValue.value > 0
        ? t("components.price_breakdown.delivery_fee_wave", {
            deliveryFee: toMoney(fee.value, fee.currency),
            basketValue: toMoney(basketValue.value, basketValue.currency),
          })
        : "",
  };
};

const chargesEntry = computed<ISatPriceBreakdownProps["items"]>(() => {
  const totals: ISatPriceBreakdownProps["items"] = [];

  if (props.loyaltyRedeemed.value > 0 || props.discountedAmount.value > 0) {
    totals.push({
      name: t("components.price_breakdown.charges_products_value"),
      amount: props.productsTotal,
    });
  }

  if (props.loyaltyRedeemed.value > 0) {
    totals.push({
      name: t("components.price_breakdown.charges_loyalty"),
      color: "color-error",
      amount: {
        value: props.loyaltyRedeemed.value * -1,
        currency: props.loyaltyRedeemed.currency,
      },
    });
  }

  if (props.discountedAmount.value > 0) {
    totals.push({
      name: t("components.price_breakdown.charges_discount"),
      color: "color-error",
      amount: {
        value: props.discountedAmount.value * -1,
        currency: props.discountedAmount.currency,
      },
    });
  }

  totals.push({
    name: t("components.price_breakdown.charges_subtotal"),
    amount: props.subtotal,
    hint: props.isTaxIncluded
      ? t("components.price_breakdown.inclusive_of_tax")
      : "",
  });

  if (props.taxAmount.value > 0) {
    totals.push({
      name: t("components.price_breakdown.tax"),
      amount: props.taxAmount,
    });
  }

  const rangesOutput = extractFeeRanges();
  if (props.deliveryFee.value > 0 || rangesOutput) {
    totals.push({
      name: t("components.price_breakdown.charges_delivery_fee"),
      amount: props.deliveryFee,
      strikedAmount: rangesOutput?.strikedAmount,
      subline: rangesOutput?.subline,
    });
  }

  if (props.serviceCharges.value > 0) {
    totals.push({
      name: feeChargeLabel("serviceCharges"),
      amount: props.serviceCharges,
    });
  }

  if (props.bagFee.value > 0) {
    totals.push({
      name: feeChargeLabel("bagFee"),
      amount: props.bagFee,
    });
  }

  if (props.tipAmount && props.tipAmount.value > 0) {
    totals.push({
      name: t("components.price_breakdown.charges_tips"),
      amount: props.tipAmount,
    });
  }

  if (!props.hideFinalTotal) {
    totals.push({
      name: t("components.price_breakdown.charges_total"),
      amount: props.total,
      color: "color-text-body",
      bold: true,
    });
  }

  if (props.giftCard?.value) {
    totals.push({
      name: t("components.price_breakdown.gift_card"),
      color: "color-text-body",
      amount: {
        value: props.giftCard.value * -1,
        currency: props.giftCard.currency,
      },
    });
  }

  if (!props.hideFinalTotal && props.outstandingBalance && props.giftCard) {
    totals.push({
      name: t("components.price_breakdown.outstanding"),
      bold: true,
      color: "color-text-body",
      borderTop: true,
      amount: {
        value: props.tipAmount
          ? props.outstandingBalance.value + props.tipAmount.value
          : props.outstandingBalance.value,
        currency: props.outstandingBalance.currency,
      },
    });
  }

  return totals;
});
</script>

<template>
  <SatPriceBreakdown :class="css('')" :items="chargesEntry" />
</template>
